import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

/**
 * Public download page for 目异同学 app. No auth required.
 * Download URLs are loaded from GET /api/app/version (same source as native app upgrade).
 */
const DEFAULT_ANDROID_APK_URL =
  'https://coca-public.oss-cn-beijing.aliyuncs.com/muyi_tencent.apk';
const FALLBACK_ANDROID_URL =
  (import.meta.env.VITE_APP_ANDROID_DOWNLOAD_URL as string) || DEFAULT_ANDROID_APK_URL;
const FALLBACK_IOS_URL = 'https://testflight.apple.com/join/c6wqQHkh';

/** Matches AppVersionDto from coca-service GET /api/app/version */
type AppVersionApiResponse = {
  minVersionCode: number;
  minVersionCodeAndroid?: number;
  minVersionCodeIos?: number;
  androidDownloadUrl: string;
  iosDownloadUrl: string;
  harmonyDownloadUrl: string | null;
};

export const DownloadPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [androidDownloadUrl, setAndroidDownloadUrl] = useState(FALLBACK_ANDROID_URL);
  const [iosDownloadUrl, setIosDownloadUrl] = useState(FALLBACK_IOS_URL);
  const [harmonyDownloadUrl, setHarmonyDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    document.title = '下载 - 目异同学';
    return () => {
      document.title = '目异同学';
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/app/version')
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((data: AppVersionApiResponse) => {
        if (cancelled) return;
        if (typeof data.androidDownloadUrl === 'string' && data.androidDownloadUrl.length > 0) {
          setAndroidDownloadUrl(data.androidDownloadUrl);
        }
        if (typeof data.iosDownloadUrl === 'string' && data.iosDownloadUrl.length > 0) {
          setIosDownloadUrl(data.iosDownloadUrl);
        }
        setHarmonyDownloadUrl(
          typeof data.harmonyDownloadUrl === 'string' && data.harmonyDownloadUrl.length > 0
            ? data.harmonyDownloadUrl
            : null,
        );
      })
      .catch(() => {
        if (!cancelled) {
          console.warn('[coca-web:DownloadPage] /api/app/version failed, using fallback URLs');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const hasAndroidUrl = androidDownloadUrl.length > 0;

  return (
    <div className="h-screen min-h-screen bg-[#F4F1EC] text-[#333] overflow-y-auto overflow-x-hidden flex flex-col">
      <a
        href="#download-main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-20 focus:px-3 focus:py-2 focus:bg-[#F4F1EC] focus:ring-2 focus:ring-[#333] focus:rounded"
      >
        跳过至主内容
      </a>
      <header className="sticky top-0 z-10 border-b border-[#e5e2dd] bg-[#F4F1EC]/95 backdrop-blur py-3 px-4 shrink-0">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link
            to="/"
            className="text-[#666] hover:text-[#333] text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#333] focus-visible:ring-offset-2 rounded"
          >
            ← 返回
          </Link>
          <h1 className="text-lg font-semibold text-balance">下载</h1>
          <span className="w-10" aria-hidden />
        </div>
      </header>

      <main id="download-main" className="max-w-3xl mx-auto px-4 py-8 pb-24 flex-1 min-h-0 scroll-mt-4">
        <p className="text-[#666] leading-relaxed mb-8">
          选择您的设备类型，获取目异同学客户端。
        </p>

        {loading ? (
          <p className="text-[#666] text-sm" role="status" aria-live="polite">
            正在获取下载链接…
          </p>
        ) : null}

        {/* Android */}
        <section
          className={`space-y-4 mb-10 ${loading ? 'opacity-50 pointer-events-none' : ''}`}
          aria-labelledby="download-android-heading"
          aria-busy={loading}
        >
          <h2 id="download-android-heading" className="text-xl font-semibold border-b border-[#ddd] pb-2 scroll-mt-4">
            Android
          </h2>
          {hasAndroidUrl ? (
            <>
              <p className="leading-relaxed text-[#555]">
                点击下方按钮或使用手机扫描二维码下载 Android 安装包（APK）。
              </p>
              <div className="flex flex-col items-center gap-3">
                <QRCodeSVG
                  value={androidDownloadUrl}
                  size={160}
                  level="M"
                  role="img"
                  aria-label="QR code for Android APK download"
                  className="rounded border border-[#e5e2dd] bg-white p-2"
                />
                <a
                  href={androidDownloadUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-[#333] text-white text-sm font-medium rounded hover:bg-[#111] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#333] focus-visible:ring-offset-2"
                >
                  下载链接
                </a>
              </div>
            </>
          ) : (
            <p className="text-[#666] leading-relaxed">
              Android 下载链接即将提供，敬请期待。
            </p>
          )}
        </section>

        {/* iOS – TestFlight */}
        <section
          className={`space-y-4 mb-10 ${loading ? 'opacity-50 pointer-events-none' : ''}`}
          aria-labelledby="download-ios-heading"
          aria-busy={loading}
        >
          <h2 id="download-ios-heading" className="text-xl font-semibold border-b border-[#ddd] pb-2 scroll-mt-4">
            iOS（TestFlight 版）
          </h2>

          <div className="bg-white/70 border border-[#e5e2dd] rounded-lg p-4 space-y-3 text-sm leading-relaxed text-[#555]">
            <p>
              <strong className="text-[#333]">什么是 TestFlight？</strong><br />
              TestFlight 是 <strong>Apple 官方</strong>提供的应用测试平台，所有通过 TestFlight 分发的应用均经过
              Apple 审核与签名，与 App Store 应用享有同等级别的安全保障。您的隐私与设备安全不会受到任何影响。
            </p>
            <p>
              <strong className="text-[#333]">如何安装？</strong>
            </p>
            <ol className="list-decimal list-inside space-y-1 pl-1">
              <li>
                在 App Store 搜索并安装{' '}
                <a
                  href="https://apps.apple.com/app/testflight/id899247664"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline underline-offset-2 hover:text-blue-800"
                >
                  TestFlight
                </a>
                （如已安装可跳过）
              </li>
              <li>点击下方链接或扫描二维码，在 TestFlight 中打开</li>
              <li>点击「接受」→「安装」即可开始使用</li>
            </ol>
            <p className="text-xs text-[#999]">
              测试版应用每个构建版本有效期 90 天，届时可更新至最新版本继续使用。需要 iOS 16 及以上系统。
            </p>
          </div>

          <div className="flex flex-col items-center gap-3">
            <QRCodeSVG
              value={iosDownloadUrl}
              size={160}
              level="M"
              role="img"
              aria-label="QR code for iOS TestFlight invitation"
              className="rounded border border-[#e5e2dd] bg-white p-2"
            />
            <a
              href={iosDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-5 py-2.5 bg-[#333] text-white text-sm font-medium rounded hover:bg-[#111] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#333] focus-visible:ring-offset-2"
            >
              通过 TestFlight 安装
            </a>
          </div>
        </section>

        {/* HarmonyOS */}
        <section
          className={`space-y-4 ${loading ? 'opacity-50 pointer-events-none' : ''}`}
          aria-labelledby="download-harmony-heading"
          aria-busy={loading}
        >
          <h2 id="download-harmony-heading" className="text-xl font-semibold border-b border-[#ddd] pb-2 scroll-mt-4">
            鸿蒙
          </h2>
          {harmonyDownloadUrl != null && harmonyDownloadUrl.length > 0 ? (
            <>
              <p className="leading-relaxed text-[#555]">
                点击下方按钮或使用手机扫描二维码下载鸿蒙版本安装包。
              </p>
              <div className="flex flex-col items-center gap-3">
                <QRCodeSVG
                  value={harmonyDownloadUrl}
                  size={160}
                  level="M"
                  role="img"
                  aria-label="QR code for HarmonyOS app download"
                  className="rounded border border-[#e5e2dd] bg-white p-2"
                />
                <a
                  href={harmonyDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-5 py-2.5 bg-[#333] text-white text-sm font-medium rounded hover:bg-[#111] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#333] focus-visible:ring-offset-2"
                >
                  下载链接
                </a>
              </div>
            </>
          ) : (
            <p className="text-[#666] leading-relaxed">
              敬请期待，后续将提供鸿蒙版本下载。
            </p>
          )}
        </section>
      </main>
    </div>
  );
};

export default DownloadPage;
